import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useNavigate } from "react-router-dom";

import { injectSpeedInsights } from "@vercel/speed-insights";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import useAuthStore from "./stores/authStore";
import { authAPI } from "./services/api";
import SecurityOverlay from "./components/SecurityOverlay";
import UpgradeModal from "./components/UpgradeModal";
import BillingProfileModal from "./components/BillingProfileModal";
import ErrorBoundary from "./components/ErrorBoundary";
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function DeepLinkHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let listenerHandle;
    CapacitorApp.addListener("appUrlOpen", (data) => {
      const url = new URL(data.url);
      navigate(url.pathname + url.search, { replace: true });
    }).then((h) => { listenerHandle = h; });
    return () => { listenerHandle?.remove(); };
  }, [navigate]);
  return null;
}

const Login            = lazy(() => import("./pages/Login"));
const Register         = lazy(() => import("./pages/Register"));
const Dashboard        = lazy(() => import("./pages/Dashboard"));
const Expenses         = lazy(() => import("./pages/Expenses"));
const RecurringExpenses= lazy(() => import("./pages/RecurringExpenses"));
const UrssafProjection = lazy(() => import("./pages/UrssafProjection"));
const ElectricCharges  = lazy(() => import("./pages/ElectricCharges"));
const Plans            = lazy(() => import("./pages/Plans"));
const FleetDashboard   = lazy(() => import("./pages/FleetDashboard"));
const QuoteDetail      = lazy(() => import("./pages/QuoteDetail"));
const InvoiceDetail    = lazy(() => import("./pages/InvoiceDetail"));
const ClientDetail     = lazy(() => import("./pages/ClientDetail"));
const SupplierDetail   = lazy(() => import("./pages/SupplierDetail"));
const Billing          = lazy(() => import("./pages/Billing"));
const NewInvoiceFlow   = lazy(() => import("./pages/billing/NewInvoiceFlow"));
const NewQuoteFlow     = lazy(() => import("./pages/billing/NewQuoteFlow"));
const NewClientFlow    = lazy(() => import("./pages/billing/NewClientFlow"));
const Receipts         = lazy(() => import("./pages/Receipts"));
const ReceiptScan      = lazy(() => import("./pages/ReceiptScan"));
const Accounting       = lazy(() => import("./pages/Accounting"));
const AcceptCollaborator = lazy(() => import("./pages/AcceptCollaborator"));
const MyClients         = lazy(() => import("./pages/MyClients"));
const ForgotPassword   = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword    = lazy(() => import("./pages/ResetPassword"));
const Admin            = lazy(() => import("./pages/Admin"));
const AdminLayout      = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard   = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers       = lazy(() => import("./pages/admin/AdminUsers"));
const AdminCommercial  = lazy(() => import("./pages/admin/AdminCommercial"));
const AdminSupport     = lazy(() => import("./pages/admin/AdminSupport"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminLogs        = lazy(() => import("./pages/admin/AdminLogs"));
const AdminBugs        = lazy(() => import("./pages/admin/AdminBugs"));
const AdminAnalytics   = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminMonitoring  = lazy(() => import("./pages/admin/AdminMonitoring"));
const AdminSecurity    = lazy(() => import("./pages/admin/AdminSecurity"));
const AdminAudit       = lazy(() => import("./pages/admin/AdminAudit"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminMetrics     = lazy(() => import("./pages/admin/AdminMetrics"));
const AdminEmailFailed = lazy(() => import("./pages/admin/AdminEmailFailed"));
const AdminWaitlist    = lazy(() => import("./pages/admin/AdminWaitlist"));
const Profile          = lazy(() => import("./pages/Profile"));
const MentionsLegales  = lazy(() => import("./pages/legal/MentionsLegales"));
const CGV              = lazy(() => import("./pages/legal/CGV"));
const Privacy          = lazy(() => import("./pages/legal/Privacy"));
const Cookies          = lazy(() => import("./pages/legal/Cookies"));
const CompanyCard       = lazy(() => import("./pages/public/CompanyCard"));
const Landing          = lazy(() => import("./pages/Landing"));
const ComingSoon       = lazy(() => import("./pages/ComingSoon"));
const Blog             = lazy(() => import("./pages/Blog"));
const BlogArticle      = lazy(() => import("./pages/BlogArticle"));
const FacturationElectronique2026 = lazy(() => import("./pages/FacturationElectronique2026"));
const FacturxInbox = lazy(() => import("./pages/FacturxInbox"));
const Success          = lazy(() => import("./pages/Success"));
const Cancel           = lazy(() => import("./pages/Cancel"));
const VerifyEmail      = lazy(() => import("./pages/VerifyEmail"));

const PageLoader = (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: "#0A0A0A" }}>
    <div style={{ color: "#158d38", fontFamily: "system-ui, sans-serif", fontSize: "14px" }}>Chargement...</div>
  </div>
);


function PrivateRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? children : <Navigate to="/login" />;
}

// Page d'accueil ("/") : sur le site web, c'est la Landing marketing.
// Dans l'appli mobile (Capacitor), il n'y a aucune raison de montrer cette
// page — l'utilisateur ouvre l'appli pour se connecter ou créer un compte,
// pas pour lire une page de vente. On saute donc directement vers /login
// (déjà connecté -> /dashboard) uniquement sur la plateforme native ; le
// site balshee.com garde exactement le même comportement qu'avant.
function HomeRoute() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (Capacitor.isNativePlatform()) {
    return <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />;
  }
  return <Landing />;
}

function AdminRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user === null) return null; // attendre le chargement du profil
  if (user.role !== "superadmin") return <Navigate to="/dashboard" />;
  return children;
}

function DriverRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user === null) return null; // attendre le chargement du profil
  if (!user.is_driver_mode) return <Navigate to="/dashboard" />;
  return children;
}

function RedirectInvoice() { const { id } = useParams(); return <Navigate to={`/billing/invoice/${id}`} replace />; }
function RedirectQuote()   { const { id } = useParams(); return <Navigate to={`/billing/quote/${id}`}   replace />; }
function RedirectClient()  { const { id } = useParams(); return <Navigate to={`/billing/client/${id}`}  replace />; }

injectSpeedInsights();

export default function App() {
  const darkMode = useAuthStore((s) => s.darkMode);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setUser = useAuthStore((s) => s.setUser);

  // Charger le profil au démarrage pour toutes les pages protégées
  useEffect(() => {
    if (isAuthenticated) {
      authAPI.me().then(res => setUser(res.data)).catch(() => {});
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  // Helper : wraps a page in its own ErrorBoundary so crashes stay isolated per route
  const eb = (label, element) => (
    <ErrorBoundary label={label}>{element}</ErrorBoundary>
  );

  return (
    <BrowserRouter>
      <Suspense fallback={PageLoader}>
      <ScrollToTop />
      <DeepLinkHandler />
      <Routes>
        {/* ── Public / Auth ── */}
        <Route path="/login"           element={<Login />} />
        <Route path="/verify-email"    element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password"  element={<ResetPassword />} />
        <Route path="/register"        element={<Register />} />

        {/* ── App core — chaque route a sa propre ErrorBoundary ── */}
        <Route path="/dashboard"
          element={<PrivateRoute>{eb("le dashboard", <Dashboard />)}</PrivateRoute>} />
        <Route path="/expenses"
          element={<PrivateRoute>{eb("les dépenses", <Expenses />)}</PrivateRoute>} />
        <Route path="/recurring-expenses"
          element={<PrivateRoute>{eb("les charges récurrentes", <RecurringExpenses />)}</PrivateRoute>} />
        <Route path="/urssaf"
          element={<PrivateRoute>{eb("la projection URSSAF", <UrssafProjection />)}</PrivateRoute>} />
        <Route path="/electric-charges"
          element={<DriverRoute>{eb("les charges électriques", <ElectricCharges />)}</DriverRoute>} />

        {/* ── Billing hub ── */}
        <Route path="/billing"
          element={<PrivateRoute>{eb("la facturation", <Billing />)}</PrivateRoute>} />
        <Route path="/billing/invoice/new"
          element={<PrivateRoute>{eb("la nouvelle facture", <NewInvoiceFlow />)}</PrivateRoute>} />
        <Route path="/billing/invoice/:id"
          element={<PrivateRoute>{eb("la facture", <InvoiceDetail />)}</PrivateRoute>} />
        <Route path="/billing/quote/new"
          element={<PrivateRoute>{eb("le nouveau devis", <NewQuoteFlow />)}</PrivateRoute>} />
        <Route path="/billing/quote/:id"
          element={<PrivateRoute>{eb("le devis", <QuoteDetail />)}</PrivateRoute>} />
        <Route path="/billing/client/new"
          element={<PrivateRoute>{eb("le nouveau client", <NewClientFlow />)}</PrivateRoute>} />
        <Route path="/billing/client/:id"
          element={<PrivateRoute>{eb("le client", <ClientDetail />)}</PrivateRoute>} />
        {/* Un seul composant (SupplierDetail) gère création ET édition — pas
            de route statique séparée pour "new" comme pour les clients :
            une route statique n'aurait pas passé id="new" via useParams(),
            SupplierDetail se serait retrouvé avec id=undefined et une page
            blanche (bug constaté, corrigé ici). */}
        <Route path="/billing/supplier/:id"
          element={<PrivateRoute>{eb("le fournisseur", <SupplierDetail />)}</PrivateRoute>} />

        {/* ── Legacy routes → redirect ── */}
        <Route path="/quotes"       element={<Navigate to="/billing" replace />} />
        <Route path="/quotes/new"   element={<Navigate to="/billing/quote/new" replace />} />
        <Route path="/quotes/:id"   element={<PrivateRoute><RedirectQuote /></PrivateRoute>} />
        <Route path="/invoices"     element={<Navigate to="/billing" replace />} />
        <Route path="/invoices/new" element={<Navigate to="/billing/invoice/new" replace />} />
        <Route path="/invoices/:id" element={<PrivateRoute><RedirectInvoice /></PrivateRoute>} />
        <Route path="/clients"      element={<Navigate to="/billing" replace />} />
        <Route path="/clients/new"  element={<Navigate to="/billing/client/new" replace />} />
        <Route path="/clients/:id"  element={<PrivateRoute><RedirectClient /></PrivateRoute>} />

        {/* ── Autres pages app ── */}
        <Route path="/receipts"
          element={<PrivateRoute>{eb("les justificatifs", <Receipts />)}</PrivateRoute>} />
        <Route path="/receipts/scan"
          element={<PrivateRoute>{eb("le scanner", <ReceiptScan />)}</PrivateRoute>} />
        <Route path="/accounting"
          element={<PrivateRoute>{eb("la comptabilité", <Accounting />)}</PrivateRoute>} />
        <Route path="/comptable/accepter/:token"
          element={eb("l'invitation comptable", <AcceptCollaborator />)} />
        <Route path="/comptable/dossiers"
          element={<PrivateRoute>{eb("mes dossiers clients", <MyClients />)}</PrivateRoute>} />
        <Route path="/facturx/inbox"
          element={<PrivateRoute>{eb("Factur-X inbox", <FacturxInbox />)}</PrivateRoute>} />
        <Route path="/plans"
          element={eb("les plans", <Plans />)} />
        <Route path="/fleet"
          element={<PrivateRoute>{eb("la flotte", <FleetDashboard />)}</PrivateRoute>} />
        <Route path="/profile"
          element={<PrivateRoute>{eb("le profil", <Profile />)}</PrivateRoute>} />

        {/* ── Admin ── */}
        <Route path="/admin-legacy" element={<AdminRoute><Admin /></AdminRoute>} />
        <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index                  element={eb("le dashboard admin",    <AdminDashboard />)} />
          <Route path="metrics"         element={eb("les métriques",         <AdminMetrics />)} />
          <Route path="users"           element={eb("les utilisateurs",      <AdminUsers />)} />
          <Route path="commercial"      element={eb("le commercial",         <AdminCommercial />)} />
          <Route path="support"         element={eb("le support",            <AdminSupport />)} />
          <Route path="subscriptions"   element={eb("les abonnements",       <AdminSubscriptions />)} />
          <Route path="logs"            element={eb("les logs",              <AdminLogs />)} />
          <Route path="bugs"            element={eb("les bugs",              <AdminBugs />)} />
          <Route path="analytics"       element={eb("les analytics",         <AdminAnalytics />)} />
          <Route path="monitoring"      element={eb("le monitoring",         <AdminMonitoring />)} />
          <Route path="security"        element={eb("la sécurité",           <AdminSecurity />)} />
          <Route path="audit"           element={eb("l'audit",               <AdminAudit />)} />
          <Route path="notifications"   element={eb("les notifications",     <AdminNotifications />)} />
          <Route path="email-failed"    element={eb("les emails échoués",    <AdminEmailFailed />)} />
          <Route path="waitlist"        element={eb("la liste d'attente",    <AdminWaitlist />)} />
        </Route>

        {/* ── Stripe / Legal / Public ── */}
        <Route path="/success" element={<PrivateRoute><Success /></PrivateRoute>} />
        <Route path="/cancel"  element={<PrivateRoute><Cancel /></PrivateRoute>} />
        <Route path="/legal/mentions" element={<MentionsLegales />} />
        <Route path="/legal/cgv"      element={<CGV />} />
        <Route path="/legal/privacy"  element={<Privacy />} />
        <Route path="/legal/cookies"  element={<Cookies />} />
        <Route path="/c/:token"      element={<CompanyCard />} />
        <Route path="/"          element={<HomeRoute />} />
        <Route path="/blog"      element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/facturation-electronique-2026" element={<FacturationElectronique2026 />} />
        <Route path="/cpam" element={<PrivateRoute><ComingSoon page="cpam" /></PrivateRoute>} />
        <Route path="/bank" element={<PrivateRoute><ComingSoon page="bank" /></PrivateRoute>} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </Suspense>
      <SecurityOverlay />
      <UpgradeModal />
      <BillingProfileModal />
    </BrowserRouter>
  );
}
